<template>
    <div class="plant-compare-details">
        <div class="plant-holder header-compare">
            <div class="column-spacing" />
            <div class="column-size" v-for="(plant, index) in plants" :key="plant.plantInfo?.id ?? index">
                <plant-details-title
                    :relatedStoretrackDetails="plant.relatedStoretrackDetails"
                    :plantInfo="plant.plantInfo"
                    comparison-mode
                    :basePlantName="plant.plantInfo?.name"
                />
            </div>
        </div>
        <table class="combined-table no-highlight-on-hover">
            <tbody>
                <tr
                    v-for="(value, key) in plantMetrics"
                    :key="key">
                    <td>{{ key }}:</td>
                    <td v-for="(metric, index) in value" :key="index">{{ metric || '-' }}</td>
                </tr>
            </tbody>
        </table>
        <div class="plant-holder selectors">
            <div class="column-spacing" />
            <bm-unit-data-headers
                class="column-size"
                :plantInfo="plants"
                v-model:active-details-tab="activeDetailsTab"
                v-model:active-sub-details-tab="activeSubDetailsTab"
                v-model:chart-category="charts"
            />
        </div>
        <div class="plant-holder">
            <div class="column-spacing" />
            <div class="column-size" v-for="(plant, plantId) in plants" :key="plantId">
                <bm-unit-data-visual
                    v-if="plant.plantInfo"
                    :plant-bmu-id="plantId"
                    :plant-info="plant.plantInfo"
                    :date-override="dateOverride"
                    :chart-date-range="chartDateRange"
                    :active-details-tab="activeDetailsTab"
                    :active-sub-details-tab="activeSubDetailsTab"
                    :charts="charts"
                    :multiView="true"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IPlantDetailsPlusRelated } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { getHeaderProperties, usePlantDetails } from '@/composables/powerMarkets/usePlantDetails.ts';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import BmUnitDataHeaders from './BmUnitDataHeaders.vue';
import BmUnitDataVisual from './BmUnitDataVisual.vue';
import PlantDetailsTitle from './PlantDetailsTitle.vue';

const props = defineProps<{
    plants: Record<string, IPlantDetailsPlusRelated>;
    dateOverride?: IDashboardOverrides;
    chartDateRange?: IDateRange;
}>();

const activeDetailsTab = ref<PlantDetailsTab | ''>('');
const activeSubDetailsTab = ref<string>('');
const charts = ref<string[]>([]);

const plantMetrics = computed(() => {
    const plantList = Object.values(props.plants);
    const keys = new Set<string>();
    const propsByPlant = plantList.map(plant => {
        const {
            technologyOrFuelValue, plantIsStorage, isMultipleBmUnitsView, decommissioningStatus,
        } = usePlantDetails(plant.plantInfo, plant.relatedEnactData, plant.relatedStoretrackDetails);

        const properties = getHeaderProperties(
            technologyOrFuelValue.value,
            plant.plantInfo?.generationCapacity,
            plant.plantInfo?.batteryEnergy,
            plant.plantInfo?.demandCapacity,
            plantIsStorage.value,
            true,
            true,
            isMultipleBmUnitsView.value,
            decommissioningStatus.value,
            plant.plantInfo?.lastZero,
            plant.plantInfo?.zone,
            plant.plantInfo?.extraInfo,
        );
        Object.keys(properties).forEach(k => keys.add(k));
        return properties;
    });

    const grouped: Record<string, (string | number | null)[]> = {};
    keys.forEach(k => {
        grouped[k] = propsByPlant.map(p => (k in p ? p[k] : null));
    });
    return grouped;
});

</script>

<style lang="scss" scoped>
.plant-compare-details {
    position: relative;
    min-width: 100%;
    width: fit-content;

    .column-spacing {
        width: 10rem;
        flex: 0 0 auto;
    }

    .column-size {
        min-width: 30rem;
        width: 100%;
    }

    .combined-table {
        td {
            &:first-child {
                width: calc(var(--smallSpacing) + 10rem);
                font-weight: var(--extraBoldFontWeight);
            }
            &:not(:first-child) {
                min-width: calc(var(--smallSpacing) + 30rem);
            }
        }
    }

    .plant-holder {
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        gap: var(--smallSpacing);
        &.header-compare {
            position: sticky;
            top: 0;
            left: 0;
            z-index: 2;
            .column-size {
                padding: var(--defaultSpacing) var(--defaultSpacing) 0 var(--defaultSpacing);
                background-color: var(--neutral400);
            }
        }
        &.selectors {
            position: sticky;
            top: 4.4rem;
            z-index: 2;
            margin-bottom: var(--smallSpacing);
            width: 100%;

            .column-size {
                background-color: var(--neutral200);
                border-bottom: solid 1px var(--neutral300);
            }
        }
    }
}
</style>
